/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { useState } from "react";
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Eye, EyeOff } from "lucide-react";
import { assets } from "../assets/assets";
import { post, storeAuthData } from "../utils/service";

interface LoginResponse {
  success: boolean;
  message: string;
  data: {
    user: {
      id: string;
      first_name: string;
      last_name: string;
      email: string;
      phone: string;
      role: string;
      preferred_currency: string;
      is_active: boolean;
      created_at: string;
      updated_at: string;
    };
    token: string;
    refresh_token: string;
  };
  timestamp: string;
}

const GlassInputWrapper = ({ children }: { children: React.ReactNode }) => (
  <div className="rounded-2xl border border-gray-200 bg-white transition-colors focus-within:border-[#ff6d27] focus-within:bg-white">
    {children}
  </div>
);


const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);


  const validateEmail = (value: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  };

  const handleSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    let valid = true;

    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address.");
      valid = false;
    } else {
      setEmailError("");
    }

    if (valid) {
      setLoading(true);
      
      try {
        const loginResponse = await post<LoginResponse>('/auth/login', {
          email,
          password
        });

        if (loginResponse.success) {
          const success = storeAuthData(loginResponse, rememberMe);
          
          if (success) {
            toast.success(loginResponse.message);
            navigate('/');
            
            setEmail("");
            setPassword("");
          }
        }
      } catch (error: any) {
        if (error.response?.data?.message) {
          toast.error(error.response.data.message);
        }
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="h-screen flex flex-col md:flex-row font-geist w-screen bg-white">
      <section className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="flex flex-col gap-6">
            <div className="animate-element animate-delay-50 flex justify-start">
              <img
                src={assets.MainLogo}
                alt="Logo"
                className="h-16 w-auto object-contain"
              />
            </div>
            
            <h1 className="animate-element animate-delay-100 text-4xl md:text-5xl font-semibold leading-tight text-gray-900">
              <span className="font-light tracking-tighter">Welcome Back</span>
            </h1>
            <p className="animate-element animate-delay-200 text-gray-600 text-xs">
              Access your account and continue managing your sales projects
            </p>

            <form className="space-y-5" onSubmit={handleSignIn}>
              <div className="animate-element animate-delay-300">
                <label className="text-xs font-medium text-gray-700">Email Address</label>
                <GlassInputWrapper>
                  <input
                    name="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="w-full bg-transparent text-xs p-4 rounded-2xl focus:outline-none text-gray-900"
                    disabled={loading}
                    required
                  />
                </GlassInputWrapper>
                {emailError && (
                  <p className="text-red-500 text-xs mt-1">{emailError}</p>
                )}
              </div>

              <div className="animate-element animate-delay-400">
                <label className="text-xs font-medium text-gray-700">Password</label>
                <GlassInputWrapper>
                  <div className="relative">
                    <input
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      className="w-full bg-transparent text-xs p-4 pr-12 rounded-2xl focus:outline-none text-gray-900"
                      disabled={loading}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-3 flex items-center"
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4 text-gray-400 hover:text-gray-900 transition-colors" />
                      ) : (
                        <Eye className="w-4 h-4 text-gray-400 hover:text-gray-900 transition-colors" />
                      )}
                    </button>
                  </div>
                </GlassInputWrapper>
              </div>

              <div className="animate-element animate-delay-500 flex items-center justify-between text-xs">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="rememberMe"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    disabled={loading}
                    className="h-3.5 w-3.5 rounded border-gray-300 text-[#ff6d27] focus:ring-2 focus:ring-[#ff6d27]"
                  />
                  <span className="text-gray-700">Keep me signed in</span>
                </label>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="animate-element animate-delay-600 w-full rounded-2xl bg-[#ff6d27] py-4 font-medium text-white hover:bg-[#E85A1B] transition-colors text-xs disabled:opacity-50"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
          </div>
        </div>
      </section>

      <section className="hidden md:block flex-1 relative p-4">
        <div
          className="animate-slide-right animate-delay-300 absolute inset-4 rounded-3xl bg-cover bg-center shadow-2xl"
          style={{ backgroundImage: `url(${assets.LoginBanner})` }}
        ></div>
      </section>
    </div>
  );
};

export default Login;